import { useState } from "react";
import { Eye, EyeSlash, Backspace } from "@phosphor-icons/react";

/**
 * Numeric on-screen keypad with an optional show/hide toggle and tactile buttons.
 * Designed for tablets with no physical keyboard.
 *
 * Props:
 * - value: current PIN string (controlled)
 * - onChange(newValue): called with the new value on every change
 * - onEnter(): called when the user presses Enter on the physical keyboard
 * - maxLength: cap for the input (default 11)
 * - allowAlphanumeric: if true, the physical keyboard can type any character
 *     (letters, symbols). The on-screen keypad still inserts digits. If false
 *     (default), only digits are accepted and pasted content is sanitized.
 * - allowShowToggle: show the eye icon to reveal (default true)
 * - placeholder: input placeholder (default "")
 * - autoFocus: focus input on mount (default true)
 * - testIdPrefix: prefix for data-testid on keypad buttons (default "pinpad")
 * - inputTestId: data-testid for the input (default "pinpad-input")
 */
export default function PinPad({
  value,
  onChange,
  onEnter,
  maxLength = 11,
  allowAlphanumeric = false,
  allowShowToggle = true,
  placeholder = "",
  autoFocus = true,
  testIdPrefix = "pinpad",
  inputTestId = "pinpad-input",
}) {
  const [show, setShow] = useState(false);

  const press = (k) => {
    if (value.length < maxLength) onChange(value + k);
  };
  const backspace = () => onChange(value.slice(0, -1));
  const clear = () => onChange("");

  const sanitize = (raw) => {
    const trimmed = raw.slice(0, maxLength);
    return allowAlphanumeric ? trimmed : trimmed.replace(/[^0-9]/g, "");
  };

  return (
    <div>
      <div className="mb-5 relative">
        <input
          type={show ? "text" : "password"}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(sanitize(e.target.value))}
          onKeyDown={(e) => e.key === "Enter" && onEnter && onEnter()}
          onPaste={(e) => {
            const text = sanitize(e.clipboardData.getData("text"));
            if (text) {
              e.preventDefault();
              onChange(text);
            }
          }}
          data-testid={inputTestId}
          inputMode={allowAlphanumeric ? "text" : "numeric"}
          pattern={allowAlphanumeric ? undefined : "[0-9]*"}
          maxLength={maxLength}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          className="w-full text-center py-4 pr-12 text-2xl font-mono tracking-widest border-b-2 bg-transparent outline-none placeholder:text-[var(--border)] placeholder:tracking-normal placeholder:text-base"
          style={{ borderColor: "var(--primary)" }}
        />
        {allowShowToggle && (
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            data-testid={`${testIdPrefix}-toggle-visibility`}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-[var(--bg)] text-[var(--text-secondary)]"
            aria-label={show ? "Ocultar" : "Mostrar"}
            tabIndex={-1}
          >
            {show ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
          <button
            key={k}
            type="button"
            data-testid={`${testIdPrefix}-${k}`}
            onClick={() => press(k)}
            className="card-flat py-4 font-display text-2xl font-semibold hover:bg-[var(--bg)] active:scale-95 transition"
          >
            {k}
          </button>
        ))}
        <button
          type="button"
          onClick={clear}
          data-testid={`${testIdPrefix}-clear`}
          className="card-flat py-4 font-display text-sm font-semibold hover:bg-[var(--bg)] active:scale-95 transition text-[var(--text-secondary)]"
        >
          Limpiar
        </button>
        <button
          type="button"
          onClick={() => press("0")}
          data-testid={`${testIdPrefix}-0`}
          className="card-flat py-4 font-display text-2xl font-semibold hover:bg-[var(--bg)] active:scale-95 transition"
        >
          0
        </button>
        <button
          type="button"
          onClick={backspace}
          data-testid={`${testIdPrefix}-backspace`}
          className="card-flat py-4 font-display text-sm font-semibold hover:bg-[var(--bg)] active:scale-95 transition text-[var(--text-secondary)] flex items-center justify-center"
        >
          <Backspace size={20} weight="duotone" />
        </button>
      </div>
    </div>
  );
}
